<template lang='pug'>
v-dialog(:value='value' @input='on_input' width='340' persistent)
  v-card.pa-3.custom-period-picker
    v-card-title.text-subtitle-1.pb-2.px-1 {{ $t('date_ranges.custom_period') }}
    v-btn-toggle.period-mode-switcher.mb-3.d-flex(
      v-if='show_mode_switcher'
      v-model='custom_period_mode'
      mandatory rounded dense
    )
      v-btn.flex-grow-1.period-mode-btn(
        v-for='m in support_period_modes'
        :key='m'
        :value='m'
        :color='PERIOD_CHIP_COLOR[m]'
        small
      ) {{ $t(`period_mode_picker.${m}`) }}

    v-date-picker.elevation-0(
      v-if='custom_period_mode === PERIOD_MODE_DAILY'
      v-model='custom_daily_range'
      :picker-date.sync='daily_picker_date'
      :min='get_min_daily_date'
      :allowed-dates='check_daily_allowed'
      :locale='language'
      range no-title full-width
    )
    v-date-picker.elevation-0(
      v-if='custom_period_mode === PERIOD_MODE_HOURLY'
      v-model='custom_single_date'
      :picker-date.sync='hourly_picker_date'
      :allowed-dates='check_hourly_allowed'
      :locale='language'
      no-title full-width
    )
    v-date-picker.elevation-0(
      v-if='custom_period_mode === PERIOD_MODE_MONTHLY'
      v-model='custom_month_range'
      :picker-date.sync='monthly_picker_date'
      :min='get_min_month'
      :max='get_max_month'
      :allowed-dates='check_monthly_allowed'
      :locale='language'
      type='month' range no-title full-width
    )

    v-row.mt-2
      v-col.d-flex.justify-space-between
        v-btn.date-btn.mr-3(
          color='grey darken-2'
          @click='on_cancel'
          large
        ) {{ $t('common.cancel') }}
        v-btn.date-btn.defult-btn-color(
          :disabled='is_custom_invalid'
          @click='on_confirm'
          large
        ) {{ $t('common.ok') }}
</template>

<script>
import {
  PERIOD_MODE_DAILY,
  PERIOD_MODE_HOURLY,
  PERIOD_MODE_MONTHLY,
  PERIOD_CHIP_COLOR,
} from '@/assets/constant/period_mode';

export default {
  name: 'custom_period_picker',

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    support_period_modes: {
      type: Array,
      default: () => [PERIOD_MODE_DAILY],
    },
    initial_mode: {
      type: String,
      default: '',
    },
    initial_daily_range: {
      type: Array,
      default: () => [],
    },
    initial_hourly_date: {
      type: String,
      default: '',
    },
    initial_monthly_range: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      PERIOD_MODE_DAILY,
      PERIOD_MODE_HOURLY,
      PERIOD_MODE_MONTHLY,
      PERIOD_CHIP_COLOR,
      language: 'en',
      custom_period_mode: PERIOD_MODE_DAILY,
      custom_daily_range: [],
      custom_single_date: '',
      custom_month_range: [],
      daily_picker_date: '',
      hourly_picker_date: '',
      monthly_picker_date: '',
    };
  },

  computed: {
    show_mode_switcher() {
      return this.support_period_modes.length > 1;
    },
    is_custom_invalid() {
      const m = this.custom_period_mode;
      if (m === PERIOD_MODE_DAILY) return !this.custom_daily_range[0] || !this.custom_daily_range[1];
      if (m === PERIOD_MODE_HOURLY) return !this.custom_single_date;
      if (m === PERIOD_MODE_MONTHLY) return !this.custom_month_range[0] || !this.custom_month_range[1];
      return false;
    },
    get_min_daily_date() {
      return this.$moment().subtract(24, 'month').format('YYYY-MM-01');
    },
    get_min_month() {
      return this.$moment().subtract(36, 'month').format('YYYY-MM');
    },
    get_max_month() {
      return this.$moment().format('YYYY-MM');
    },
  },

  watch: {
    value(p_val) {
      if (p_val) this.reset_from_props();
    },
    custom_daily_range(p_val) {
      this._fix_reversed_range(p_val);
    },
    custom_month_range(p_val) {
      this._fix_reversed_range(p_val);
    },
  },

  mounted() {
    const lang = (localStorage.getItem('language') || 'en').toLowerCase();
    this.language = lang === 'tw' ? 'zh-TW' : 'en';
  },

  methods: {
    _fix_reversed_range(p_val) {
      if (p_val[0] && p_val[1] && p_val[0] > p_val[1])
        [p_val[0], p_val[1]] = [p_val[1], p_val[0]];
    },

    reset_from_props() {
      const fallback_mode = this.support_period_modes[0] || PERIOD_MODE_DAILY;
      this.custom_period_mode = this.support_period_modes.includes(this.initial_mode)
        ? this.initial_mode
        : fallback_mode;

      this.custom_daily_range = this.initial_daily_range[0] && this.initial_daily_range[1]
        ? [...this.initial_daily_range]
        : [
          this.$moment().subtract(30, 'day').format('YYYY-MM-DD'),
          this.$moment().subtract(1, 'day').format('YYYY-MM-DD'),
        ];
      this.daily_picker_date = this.custom_daily_range[0]
        ? this.custom_daily_range[0].slice(0, 7)
        : this.$moment().format('YYYY-MM');

      this.custom_single_date = this.initial_hourly_date || this.$moment().format('YYYY-MM-DD');
      this.hourly_picker_date = this.custom_single_date.slice(0, 7);

      this.custom_month_range = this.initial_monthly_range[0] && this.initial_monthly_range[1]
        ? [...this.initial_monthly_range]
        : [
          this.$moment().subtract(1, 'year').format('YYYY-MM'),
          this.$moment().subtract(1, 'month').format('YYYY-MM'),
        ];
      this.monthly_picker_date = this.custom_month_range[0]
        ? this.custom_month_range[0].slice(0, 4)
        : this.$moment().format('YYYY');
    },

    check_daily_allowed(p_val) {
      return p_val < this.$moment().format('YYYY-MM-DD');
    },

    check_hourly_allowed(p_val) {
      return p_val <= this.$moment().format('YYYY-MM-DD');
    },

    check_monthly_allowed(p_val) {
      return p_val <= this.$moment().format('YYYY-MM');
    },

    on_input(p_val) {
      this.$emit('input', p_val);
    },

    on_cancel() {
      this.$emit('input', false);
    },

    on_confirm() {
      const mode = this.custom_period_mode;
      const value_map = {
        [PERIOD_MODE_DAILY]: () => [...this.custom_daily_range],
        [PERIOD_MODE_HOURLY]: () => this.custom_single_date,
        [PERIOD_MODE_MONTHLY]: () => [...this.custom_month_range],
      };
      this.$emit('confirm', { mode, value: value_map[mode]() });
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss">
/* Unscoped: v-dialog content is teleported outside the scoped parent. */
.custom-period-picker {
  .period-mode-switcher.v-btn-toggle {
    background: rgba(0, 0, 0, 0.04);
    border-radius: 28px;
    padding: 4px;
    box-shadow: none;

    .period-mode-btn.v-btn {
      min-width: 80px;
      text-transform: none;
      letter-spacing: normal;
      font-weight: 500;
      transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease, color 0.18s ease;
      color: rgba(0, 0, 0, 0.6);
      border: none !important;
      border-radius: 24px !important;

      &:not(.v-btn--active) {
        background: transparent !important;
      }

      &.v-btn--active {
        color: #fff !important;
        transform: translateY(-1px) scale(1.03);
      }
    }
  }
}

.theme--dark .custom-period-picker {
  .period-mode-switcher.v-btn-toggle {
    background: rgba(255, 255, 255, 0.06);
    .period-mode-btn.v-btn:not(.v-btn--active) {
      color: rgba(255, 255, 255, 0.7);
    }
  }
}
</style>
